import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useQuery} from 'react-query'
import {useTable, useFilters, useGlobalFilter, useSortBy} from 'react-table'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import {KTIcon} from '../../../../helpers'
import {DrawerKYCDetails} from '../drawers/DrawerKYCDetails'
import {fetchKYCList, KYC_QUERY_KEY} from '../../../../../app/pages/kycVerification/_requests'

dayjs.extend(localizedFormat)

type Props = {
  className: string
}

const TablesWidgetKYC: React.FC<Props> = ({className}) => {
  const intl = useIntl()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [kycDrawerRecord, setKycDrawerRecord] = useState<any>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const {data, isFetching} = useQuery(
    [KYC_QUERY_KEY, page, pageSize, debouncedSearch, statusFilter],
    () =>
      fetchKYCList({
        page,
        page_size: pageSize,
        ...(debouncedSearch ? {search: debouncedSearch} : {}),
        ...(statusFilter ? {status: statusFilter} : {}),
      }),
    {keepPreviousData: true}
  )

  const rows = data?.results ?? []
  const totalCount = data?.count ?? 0
  const pageCount = Math.ceil(totalCount / pageSize) || 1

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({id: 'TABLE.SL_NO', defaultMessage: 'S.No'}),
        id: 'serial-number',
        Cell: ({row}: any) => (page - 1) * pageSize + row.index + 1,
      },
      {
        Header: intl.formatMessage({id: 'TABLE.COMPANY', defaultMessage: 'Company'}),
        accessor: 'legalEntityName',
        Cell: ({row}: any) => (
          <div>
            <div
              className='fw-bold text-gray-800 text-truncate'
              style={{maxWidth: '180px'}}
              title={row.original.legalEntityName}
            >
              {row.original.legalEntityName}
            </div>
            <div
              className='text-muted fs-7 text-truncate'
              style={{maxWidth: '180px'}}
              title={row.original.ownerId ?? ''}
            >
              {row.original.ownerId ?? '--------------'}
            </div>
          </div>
        ),
      },
      {
        Header: intl.formatMessage({id: 'KYC.BUSINESS_CATEGORY', defaultMessage: 'Business Category'}),
        accessor: 'category',
      },
      {
        Header: intl.formatMessage({id: 'TABLE.VENDOR_TYPE', defaultMessage: 'Vendor Type'}),
        accessor: 'resourceType',
        Cell: ({value}: any) => <span className='text-gray-800'>{value ?? '—'}</span>,
      },
      {
        Header: intl.formatMessage({id: 'TABLE.CONTACT_PERSON', defaultMessage: 'Contact Person'}),
        accessor: 'authorisedPersonName',
        Cell: ({row}: any) => (
          <div>
            <div
              className='fw-bold text-gray-800 text-truncate'
              style={{maxWidth: '160px'}}
              title={row.original.authorisedPersonName}
            >
              {row.original.authorisedPersonName}
            </div>
            <div
              className='text-muted fs-7 text-truncate'
              style={{maxWidth: '160px'}}
              title={row.original.emailAddress}
            >
              {row.original.emailAddress}
            </div>
          </div>
        ),
      },
      {
        Header: intl.formatMessage({id: 'TABLE.SUBMITTED_ON', defaultMessage: 'Submitted On'}),
        accessor: 'submittedAt',
        Cell: ({value}: any) => (
          <>
            <span>{dayjs(value).format('DD/MM/YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>{dayjs(value).format('LT')}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({id: 'TABLE.STATUS', defaultMessage: 'Status'}),
        accessor: 'status',
        Cell: ({value}: any) =>
          value === 'approved' ? (
            <span className='badge badge-light-success fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.APPROVED', defaultMessage: 'Approved'})}
            </span>
          ) : value === 'rejected' ? (
            <span className='badge badge-light-danger fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.REJECTED', defaultMessage: 'Rejected'})}
            </span>
          ) : (
            <span className='badge badge-light-warning fw-bold px-4 py-3'>
              {intl.formatMessage({id: 'COMMON.PENDING', defaultMessage: 'Pending'})}
            </span>
          ),
      },
      {
        Header: intl.formatMessage({id: 'TABLE.ACTIONS', defaultMessage: 'Actions'}),
        id: 'actions',
        Cell: ({row}: any) => (
          <button
            type='button'
            className='btn h-35px w-65px btn-light-info text-nowrap px-0 py-0'
            id='kyc-details-toggle'
            onClick={() => setKycDrawerRecord(row.original)}
          >
            {intl.formatMessage({id: 'COMMON.VIEW', defaultMessage: 'View'})}
          </button>
        ),
      },
    ],
    [page, pageSize, intl]
  )

  const table = useTable(
    {
      columns,
      data: rows,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
    } as any,
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>
            {intl.formatMessage({id: 'KYC.TITLE', defaultMessage: 'KYB Verification'})}
          </span>
          <span className='text-muted fw-semibold fs-7'>
            {intl.formatMessage(
              {id: 'KYC.TOTAL_APPLICATIONS', defaultMessage: 'Total {count} applications'},
              {count: totalCount}
            )}
          </span>
        </h3>
        <div className='d-flex align-items-center'>
          <select
            className='form-select form-select-sm form-select-solid w-auto ms-2'
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <option value=''>
              {intl.formatMessage({id: 'COMMON.STATUS', defaultMessage: 'Status'})}
            </option>
            <option value='PENDING_REVIEW'>
              {intl.formatMessage({id: 'COMMON.PENDING', defaultMessage: 'Pending'})}
            </option>
            <option value='ACCEPTED'>
              {intl.formatMessage({id: 'COMMON.APPROVED', defaultMessage: 'Approved'})}
            </option>
            <option value='REJECTED'>
              {intl.formatMessage({id: 'COMMON.REJECTED', defaultMessage: 'Rejected'})}
            </option>
          </select>
          <div className='d-flex align-items-center position-relative my-1 ms-2'>
            <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
            <input
              type='text'
              className='form-control form-control-solid form-select-sm w-150px ps-9'
              placeholder={intl.formatMessage({
                id: 'KYC.SEARCH',
                defaultMessage: 'Search company',
              })}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...table.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {table.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps((column as any).getSortByToggleProps())}>
                      <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                      {(column as any).isSorted ? (
                        (column as any).isSortedDesc ? (
                          <KTIcon iconName='down' className='ms-1' />
                        ) : (
                          <KTIcon iconName='up' className='ms-1' />
                        )
                      ) : (
                        ''
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...table.getTableBodyProps()}>
              {isFetching ? (
                <tr>
                  <td colSpan={columns.length} className='text-center py-6'>
                    <span className='spinner-border spinner-border-sm text-primary' />
                  </td>
                </tr>
              ) : table.rows.length > 0 ? (
                table.rows.map((row) => {
                  table.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className='text-center'>
                    <span className='fw-bold'>
                      {intl.formatMessage({id: 'COMMON.NO_DATA_FOUND', defaultMessage: 'No data found.'})}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className='d-flex justify-content-between align-items-center mt-5'>
            <div>
              <select
                className='form-select form-select-sm form-select-solid'
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value))
                  setPage(1)
                }}
              >
                {[10, 25, 50, 100].map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </div>
            <div>
              <span>{intl.formatMessage({id: 'COMMON.PAGE_OF'}, {page, total: pageCount})}</span>
            </div>
            <div>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
              </button>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pageCount}
              >
                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
              </button>
            </div>
          </div>
        </div>
      </div>

      <DrawerKYCDetails kycRecord={kycDrawerRecord} />
    </div>
  )
}

export {TablesWidgetKYC}
